<template>
  <div class="list-container">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <h1 class="mb-0">IES4取込</h1>
      <!-- 操作説明はPDFで配布しており、画面からも開けるようにしている -->
      <a class="manual-link" :href="manualUrl" target="_blank" rel="noopener">操作説明（PDF）</a>
    </div>

    <!-- 対象建物は建物一覧で確定済み。IES4内の建物情報は差異警告にのみ使用する。 -->
    <div class="target-building-box my-3">
      <div class="label">対象建物</div>
      <div class="fw-bold fs-5">
        {{ buildingName || '読み込み中...' }}
        <span v-if="buildingName" class="badge text-bg-success ms-2">確定済み</span>
      </div>
      <div class="text-muted small">{{ customerName }}</div>
      <div class="text-muted small" v-if="buildingAddress">{{ buildingAddress }}</div>
    </div>

    <!-- 解析済みのときは選択済みファイルだけを表示し、差し替え時のみ選択欄を開く -->
    <div class="card my-3">
      <div class="card-body">
        <div v-if="hasResult && !showFileSelector" class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <span class="text-muted small d-block">IES4ファイル</span>
            <span class="fw-bold">{{ selectedFileName }}</span>
            <span class="badge text-bg-success ms-2">解析完了</span>
          </div>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isBusy" @click="showFileSelector = true">
            別のファイルに変更
          </button>
        </div>

        <template v-else>
          <label for="ies4File" class="form-label fw-bold">IES4ファイル</label>
          <div
            class="drop-area"
            :class="{ 'drop-area-active': isDragOver, 'drop-area-disabled': isBusy }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
          >
            <p class="mb-2">ここにIES4ファイルをドラッグ＆ドロップ、またはファイルを選択してください</p>
            <input
              id="ies4File"
              ref="fileInputRef"
              type="file"
              class="form-control"
              accept=".IES4,.ies4"
              :disabled="isBusy"
              @change="handleFileChange"
            >
            <div class="form-text">ファイルを選択すると自動で解析します（10MBまで）。別のファイルを選び直すと解析結果はリセットされます。</div>
          </div>

          <div v-if="selectedFileName" class="selected-file mt-3">
            <span class="fw-bold">{{ selectedFileName }}</span>
            <span v-if="status === 'analyzing'" class="ms-2 text-muted">解析中...</span>
            <span v-else-if="status === 'analyzeError'" class="badge text-bg-danger ms-2">解析エラー</span>
          </div>
        </template>
      </div>
    </div>

    <!-- 登録を止めるエラー -->
    <div v-if="errorMessages.length" class="alert alert-danger">
      <div class="fw-bold mb-1">登録できません</div>
      <ul class="mb-0">
        <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
      </ul>
    </div>

    <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

    <template v-if="hasResult">
      <!-- 確認して進められる警告 -->
      <div v-if="warnings.length" class="alert alert-warning">
        <div class="fw-bold mb-1">確認事項</div>
        <ul class="mb-0">
          <li v-for="(warning, index) in warnings" :key="index">{{ warning.message }}</li>
        </ul>
      </div>

      <!-- 解析結果 -->
      <div class="card my-3">
        <div class="card-header fw-bold">解析結果</div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-3"><span class="item-label">測定日</span>{{ metadata.report_date || '-' }}</div>
            <div class="col-md-3"><span class="item-label">天候</span>{{ metadata.weather || '-' }}</div>
            <div class="col-md-3"><span class="item-label">測定者</span>{{ reportParams.measure_person || '-' }}</div>
            <div class="col-md-3"><span class="item-label">補助者</span>{{ reportParams.assistant || '-' }}</div>
            <div class="col-md-3"><span class="item-label">測定地点数</span>{{ metadata.imported_work_place_count }}</div>
            <div class="col-md-3"><span class="item-label">測定値件数</span>{{ metadata.imported_measurement_count }}</div>
            <div class="col-md-3"><span class="item-label">IES4内建物名</span>{{ metadata.source_building_name || '-' }}</div>
            <div class="col-md-3">
              <span class="item-label">DataVersion</span>
              {{ metadata.data_version || '-' }}
              <span v-if="metadata.verified_data_version" class="badge text-bg-secondary ms-1">検証済</span>
              <span v-else class="badge text-bg-warning ms-1">要確認</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 重複情報 -->
      <div v-if="duplicateImport || duplicateReports.length" class="card my-3 border-warning">
        <div class="card-header fw-bold">重複情報</div>
        <div class="card-body">
          <div v-if="duplicateImport" class="mb-2">
            同一ファイルの取込履歴：{{ duplicateImport.source_file_name }}（測定日 {{ duplicateImport.measure_dt }}／取込 {{ formatDateTime(duplicateImport.imported_at) }}）
            <div class="text-muted small mt-1">
              登録時に「更新する」か「新規登録」かを選択できます。
            </div>
          </div>
          <div v-if="duplicateReports.length">
            同一建物・同一測定日の報告書が {{ duplicateReports.length }} 件あります（報告書ID：{{ duplicateReports.map(report => report.id).join('、') }}）。
          </div>
        </div>
      </div>

      <!-- 測定機器の選択 -->
      <div class="card my-3">
        <div class="card-header fw-bold">測定機器の選択</div>
        <div class="card-body">
          <p class="text-muted small">
            初期値はIES4に記載された測定機器です。マスタに無い場合は登録時に測定機器マスタを新規作成します。
            既存のマスタを使う場合は変更してください。
          </p>
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-dark">
              <tr>
                <th class="text-nowrap">測定項目</th>
                <th class="text-nowrap">IES4内の機器</th>
                <th>測定機器</th>
                <th>測定種別</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="template in usedDeviceTemplates" :key="template.measure_type_key">
                <td class="text-nowrap">{{ template.measure_type_name }}</td>
                <td class="text-nowrap text-muted small">{{ template.raw_label || '-' }}</td>
                <td>
                  <select
                    class="form-select"
                    v-model="selectedDeviceIds[template.measure_type_key]"
                    :disabled="isBusy"
                  >
                    <option value="">
                      IES4記載の機器を使う{{ template.will_create_device ? '（マスタを新規作成）' : '' }}
                    </option>
                    <optgroup v-if="template.device_candidates && template.device_candidates.length" label="候補">
                      <option v-for="device in template.device_candidates" :key="`c-${device.id}`" :value="device.id">
                        {{ deviceLabel(device) }}
                      </option>
                    </optgroup>
                    <optgroup label="すべての測定機器">
                      <option v-for="device in deviceOptions" :key="`a-${device.id}`" :value="device.id">
                        {{ deviceLabel(device) }}
                      </option>
                    </optgroup>
                  </select>
                  <div v-if="!selectedDeviceIds[template.measure_type_key] && template.will_create_device" class="mt-1">
                    <span v-if="newDeviceInputs[template.measure_type_key]" class="text-success small">
                      新規作成する機器：{{ newDeviceInputs[template.measure_type_key].device_nm }}／{{ newDeviceInputs[template.measure_type_key].manufacuturer }}
                    </span>
                    <span v-else class="text-danger small">マスタ未登録です。機器情報の入力が必要です。</span>
                    <button type="button" class="btn btn-outline-secondary btn-sm ms-2" :disabled="isBusy" @click="openDeviceModal(template)">
                      機器情報を入力
                    </button>
                  </div>
                </td>
                <td>
                  <select
                    class="form-select"
                    v-model="selectedMeasureTypeIds[template.measure_type_key]"
                    :disabled="isBusy"
                  >
                    <option value="">選択してください</option>
                    <optgroup v-if="template.measure_type_candidates && template.measure_type_candidates.length" label="候補">
                      <option v-for="measureType in template.measure_type_candidates" :key="`c-${measureType.id}`" :value="measureType.id">
                        {{ measureType.measure_type_nm }}
                      </option>
                    </optgroup>
                    <optgroup label="すべての測定種別">
                      <option v-for="measureType in measureTypeOptions" :key="`a-${measureType.id}`" :value="measureType.id">
                        {{ measureType.measure_type_nm }}
                      </option>
                    </optgroup>
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="d-flex justify-content-end gap-2 my-3">
        <button type="button" class="btn btn-secondary" :disabled="isBusy" @click="goBack">建物一覧へ戻る</button>
        <button type="button" class="btn btn-success" :disabled="isPreviewDisabled" @click="openPreview">
          プレビュー表示
        </button>
      </div>
    </template>

    <div v-else-if="status !== 'analyzing'" class="d-flex justify-content-end my-3">
      <button type="button" class="btn btn-secondary" @click="goBack">建物一覧へ戻る</button>
    </div>

    <!-- 登録前プレビュー（画面遷移せずモーダルで確認） -->
    <div ref="previewModalRef" class="modal fade" id="ies4PreviewModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-fullscreen-xl-down modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">取込プレビュー（登録される報告書の内容）</h5>
            <button type="button" class="btn-close" :disabled="status === 'saving'" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="warnings.length" class="alert alert-warning">
              <div class="fw-bold mb-1">注意点</div>
              <ul class="mb-0">
                <li v-for="(warning, index) in warnings" :key="index">{{ warning.message }}</li>
              </ul>
            </div>

            <Ies4ReportPreview
              :visible="isPreviewVisible"
              :building-name="buildingName"
              :report-params="reportParams"
              :measure-result="measureResult"
              :used-devices="previewUsedDevices"
            />
          </div>
          <div class="modal-footer justify-content-between">
            <div class="d-flex align-items-center gap-3">
              <span class="fw-bold">登録方法</span>
              <div class="form-check">
                <input id="bookRegistFlg0" class="form-check-input" type="radio" :value="0" v-model="bookRegistFlg" :disabled="status === 'saving'">
                <label class="form-check-label" for="bookRegistFlg0">仮登録</label>
              </div>
              <div class="form-check">
                <input id="bookRegistFlg1" class="form-check-input" type="radio" :value="1" v-model="bookRegistFlg" :disabled="status === 'saving'">
                <label class="form-check-label" for="bookRegistFlg1">本登録</label>
              </div>
            </div>
            <div class="d-flex gap-2">
              <button type="button" class="btn btn-secondary" :disabled="status === 'saving'" data-bs-dismiss="modal">キャンセル</button>
              <button type="button" class="btn btn-primary" :disabled="isSaveDisabled" @click="handleCommit">
                {{ status === 'saving' ? '登録中...' : '登録' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 測定機器マスタの新規登録内容を入力する -->
    <div ref="deviceModalRef" class="modal fade" tabindex="-1" data-bs-backdrop="static" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">測定機器の新規登録</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              IES4に記載された測定機器がマスタにありません。登録時に測定機器マスタへ作成する内容を入力してください。
            </p>
            <div class="mb-2">
              <label class="form-label">測定機器名 <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model.trim="deviceForm.device_nm">
            </div>
            <div class="mb-2">
              <label class="form-label">製造者 <span class="text-danger">*</span></label>
              <input type="text" class="form-control" v-model.trim="deviceForm.manufacuturer">
            </div>
            <div class="mb-2">
              <label class="form-label">型式</label>
              <input type="text" class="form-control" v-model.trim="deviceForm.model_nm">
            </div>
            <div class="mb-2">
              <label class="form-label">製造番号</label>
              <input type="text" class="form-control" v-model.trim="deviceForm.serial_no">
            </div>
            <div class="mb-2">
              <label class="form-label">仕様</label>
              <input type="text" class="form-control" v-model.trim="deviceForm.specification">
            </div>
            <div v-if="deviceFormError" class="alert alert-danger mb-0">{{ deviceFormError }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">キャンセル</button>
            <button type="button" class="btn btn-primary" @click="saveDeviceForm">OK</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 同一ファイル再取込時の確認 -->
    <div ref="duplicateModalRef" class="modal fade" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">同一ファイルの取込履歴があります</h5>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              このファイルは既に取り込まれています。
            </div>
            <table v-if="duplicateImport" class="table table-bordered table-sm mb-3">
              <tbody>
                <tr><th class="dup-th">対象の報告書</th><td>報告書ID {{ duplicateImport.t_measurement_report_id || '-' }}</td></tr>
                <tr><th class="dup-th">測定日</th><td>{{ duplicateImport.measure_dt }}</td></tr>
                <tr><th class="dup-th">登録区分</th><td>{{ duplicateImport.book_regist_flg === 1 ? '本登録' : '仮登録' }}</td></tr>
                <tr><th class="dup-th">元ファイル</th><td>{{ duplicateImport.source_file_name }}</td></tr>
                <tr><th class="dup-th">取込日時</th><td>{{ formatDateTime(duplicateImport.imported_at) }}</td></tr>
              </tbody>
            </table>
            <p class="mb-1"><span class="fw-bold">更新する</span>：既存の報告書を今回の内容で上書きします。</p>
            <p class="text-danger small">
              測定地点・測定値・使用機器・集計結果はすべて作り直されます。報告書画面で手修正した内容は失われます。
              <span v-if="duplicateImport && duplicateImport.book_regist_flg === 1" class="d-block fw-bold mt-1">
                この報告書は本登録済みです。更新してよいか必ず確認してください。
              </span>
            </p>
            <p class="mb-0"><span class="fw-bold">新規登録</span>：既存の報告書は残したまま、別の報告書として登録します。</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" :disabled="status === 'saving'" @click="closeDuplicateModal">キャンセル</button>
            <button type="button" class="btn btn-outline-primary" :disabled="status === 'saving' || !canUpdateExisting" @click="commitAs('create')">
              新規登録
            </button>
            <button type="button" class="btn btn-danger" :disabled="status === 'saving' || !canUpdateExisting" @click="commitAs('update')">
              更新する
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isBusy" class="loading-overlay">
      <div class="spinner-border text-light" role="status"></div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { Modal } from 'bootstrap';
import { getBuilding, previewMeasurementReportIes4, commitMeasurementReportIes4 } from '@/api/axios';
import { takePendingIes4File } from '@/utils/pendingIes4File';
import { IES4_IMPORT_MANUAL_URL } from '@/utils/manualLinks';
import Ies4ReportPreview from '@/components/MeasurementReports/Ies4ReportPreview.vue';

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;

export default {
  components: {
    Ies4ReportPreview
  },
  setup() {
    const title = ref('IES4取込');
    watchEffect(() => {
      document.title = title.value;
    });

    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const customerId = route.params.customer_id;
    const buildingId = route.params.building_id;

    const building = ref(null);
    const fileInputRef = ref(null);
    const previewModalRef = ref(null);
    const selectedFile = ref(null);
    const selectedFileName = ref('');
    const isDragOver = ref(false);
    const showFileSelector = ref(true);
    const isPreviewVisible = ref(false);

    // 画面状態: idle / analyzing / analyzed / analyzeError / saving / saved / saveError
    const status = ref('idle');
    const result = ref(null);
    const errorMessages = ref([]);
    const successMessage = ref('');
    const bookRegistFlg = ref(0);
    const selectedDeviceIds = reactive({});
    const selectedMeasureTypeIds = reactive({});
    // マスタ未登録の機器について、画面で入力された登録内容（測定項目キー単位で保持）
    const newDeviceInputs = reactive({});
    const deviceModalRef = ref(null);
    const duplicateModalRef = ref(null);
    const deviceForm = reactive({ device_nm: '', manufacuturer: '', model_nm: '', serial_no: '', specification: '' });
    const deviceFormError = ref('');
    const deviceFormTemplateKey = ref('');

    const isBusy = computed(() => status.value === 'analyzing' || status.value === 'saving');
    const hasResult = computed(() => result.value !== null);

    const metadata = computed(() => result.value?.metadata || {});
    const reportParams = computed(() => result.value?.report_params || {});
    const measureResult = computed(() => result.value?.measure_result || null);
    const usedDeviceTemplates = computed(() => result.value?.used_device_templates || []);
    const deviceOptions = computed(() => result.value?.device_options || []);
    const measureTypeOptions = computed(() => result.value?.measure_type_options || []);
    const warnings = computed(() => result.value?.warnings || []);
    const duplicateImport = computed(() => result.value?.duplicate_import || null);
    const duplicateReports = computed(() => result.value?.duplicate_reports || []);

    const buildingName = computed(() => result.value?.building?.building_nm || building.value?.building_nm || '');
    const customerName = computed(() => (
      result.value?.building?.customer_nm || building.value?.m_customer?.customer_nm || ''
    ));
    const buildingAddress = computed(() => {
      if (result.value?.building?.address) return result.value.building.address;
      if (!building.value) return '';
      return [building.value.prefecture, building.value.city, building.value.other_address].filter(Boolean).join('');
    });

    const deviceLabel = (device) => (
      [device.device_nm, device.model_nm, device.serial_no].filter(Boolean).join(' / ')
    );

    // プレビューの測定機器欄に表示する内容（選択済みマスタ or IES4記載の機器）
    const previewUsedDevices = computed(() => usedDeviceTemplates.value.map((template) => {
      const selectedId = selectedDeviceIds[template.measure_type_key];
      const selectedDevice = selectedId
        ? deviceOptions.value.find((device) => device.id === selectedId)
        : template.resolved_device;

      if (selectedDevice) {
        return {
          measure_type_key: template.measure_type_key,
          measure_type_name: template.measure_type_name,
          device_nm: selectedDevice.device_nm,
          detail: [selectedDevice.manufacuturer, selectedDevice.model_nm, selectedDevice.serial_no].filter(Boolean).join(' / '),
          will_create: false
        };
      }

      return {
        measure_type_key: template.measure_type_key,
        measure_type_name: template.measure_type_name,
        device_nm: template.instrument_name || '-',
        detail: `製造番号 ${template.instrument_id || '-'}`,
        will_create: Boolean(template.will_create_device)
      };
    }));

    // マスタ未登録の機器を使う行は、機器情報の入力が済んでいる必要がある
    const missingDeviceInputs = computed(() => usedDeviceTemplates.value.filter((template) => (
      !selectedDeviceIds[template.measure_type_key] &&
      template.will_create_device &&
      !newDeviceInputs[template.measure_type_key]
    )));

    const isPreviewDisabled = computed(() => isBusy.value || !hasResult.value);
    const isSaveDisabled = computed(() => {
      if (isBusy.value || !hasResult.value || !selectedFile.value) return true;
      if (missingDeviceInputs.value.length) return true;
      // 測定機器は未選択（IES4記載の機器を使う）を許容する。測定種別は必須。
      return usedDeviceTemplates.value.some((template) => !selectedMeasureTypeIds[template.measure_type_key]);
    });

    // 既存報告書が特定できないと更新できない
    const canUpdateExisting = computed(() => Boolean(duplicateImport.value?.t_measurement_report_id));

    const getModal = () => {
      const element = previewModalRef.value;
      if (!element) return null;
      return Modal.getInstance(element) || new Modal(element);
    };

    const openPreview = () => {
      getModal()?.show();
    };

    const closePreview = () => {
      getModal()?.hide();
    };

    // 再アップロード時は前回の解析結果・警告・機器選択・プレビュー状態をすべて破棄する
    const resetResult = () => {
      result.value = null;
      errorMessages.value = [];
      successMessage.value = '';
      bookRegistFlg.value = 0;
      Object.keys(selectedDeviceIds).forEach((key) => delete selectedDeviceIds[key]);
      Object.keys(selectedMeasureTypeIds).forEach((key) => delete selectedMeasureTypeIds[key]);
      Object.keys(newDeviceInputs).forEach((key) => delete newDeviceInputs[key]);
      closePreview();
      closeDuplicateModal();
    };

    const extractErrorMessages = (error) => {
      const data = error?.response?.data;
      if (!data) return ['通信に失敗しました。時間をおいて再度お試しください。'];
      if (Array.isArray(data.errors)) {
        return data.errors.map((item) => (typeof item === 'string' ? item : item.message));
      }
      return ['処理に失敗しました。'];
    };

    // 初期値はIES4記載の機器（マスタがあればそれを選択、無ければ空＝登録時に自動作成）
    const initializeMasterSelections = () => {
      usedDeviceTemplates.value.forEach((template) => {
        selectedDeviceIds[template.measure_type_key] = template.resolved_device ? template.resolved_device.id : '';
        const measureTypeCandidate = template.measure_type_candidates?.[0];
        selectedMeasureTypeIds[template.measure_type_key] = measureTypeCandidate ? measureTypeCandidate.id : '';
      });
    };

    const analyze = async (file) => {
      status.value = 'analyzing';
      errorMessages.value = [];

      try {
        const response = await previewMeasurementReportIes4({
          file,
          customerId,
          buildingId
        });
        result.value = response.data;
        initializeMasterSelections();
        status.value = 'analyzed';
        showFileSelector.value = false;
      } catch (error) {
        result.value = null;
        errorMessages.value = extractErrorMessages(error);
        status.value = 'analyzeError';
        showFileSelector.value = true;
      }
    };

    const acceptFile = async (file) => {
      resetResult();

      if (!file) {
        selectedFile.value = null;
        selectedFileName.value = '';
        status.value = 'idle';
        return;
      }

      selectedFile.value = file;
      selectedFileName.value = file.name;

      if (!file.name.toLowerCase().endsWith('.ies4')) {
        errorMessages.value = ['拡張子がIES4のファイルを選択してください'];
        status.value = 'analyzeError';
        return;
      }

      if (file.size > MAX_UPLOAD_SIZE) {
        errorMessages.value = ['ファイルサイズは10MB以下にしてください'];
        status.value = 'analyzeError';
        return;
      }

      // ファイル選択／ドロップ直後に自動解析する（「解析する」ボタンは置かない）
      await analyze(file);
    };

    const handleFileChange = async (event) => {
      await acceptFile(event.target.files?.[0] || null);
    };

    const handleDragOver = () => {
      if (isBusy.value) return;
      isDragOver.value = true;
    };

    const handleDragLeave = () => {
      isDragOver.value = false;
    };

    const handleDrop = async (event) => {
      isDragOver.value = false;
      if (isBusy.value) return;

      const file = event.dataTransfer?.files?.[0] || null;
      if (fileInputRef.value) fileInputRef.value.value = '';
      await acceptFile(file);
    };

    const selectedUsedDevices = () => usedDeviceTemplates.value.map((template, index) => ({
      // 空の場合はサーバー側でIES4記載の機器を解決・作成する
      m_device_id: selectedDeviceIds[template.measure_type_key] || null,
      m_measure_type_id: selectedMeasureTypeIds[template.measure_type_key],
      measure_type_key: template.measure_type_key,
      new_device: newDeviceInputs[template.measure_type_key] || null,
      sort_no: index + 1
    }));

    const getDuplicateModal = () => {
      const element = duplicateModalRef.value;
      if (!element) return null;
      return Modal.getInstance(element) || new Modal(element);
    };

    const closeDuplicateModal = () => {
      getDuplicateModal()?.hide();
    };

    const commitAs = async (importMode) => {
      status.value = 'saving';
      errorMessages.value = [];

      try {
        const response = await commitMeasurementReportIes4({
          file: selectedFile.value,
          customerId,
          buildingId,
          usedDevices: selectedUsedDevices(),
          bookRegistFlg: bookRegistFlg.value,
          // 新規登録を選んだ場合は重複を承知のうえで登録する
          allowDuplicate: true,
          importToken: result.value?.import_token,
          sourceFileSha256: metadata.value.source_file_sha256,
          importMode,
          targetReportId: duplicateImport.value?.t_measurement_report_id
        });

        status.value = 'saved';
        closeDuplicateModal();
        closePreview();

        const reportId = response.data?.measurement_report?.id;
        const registLabel = bookRegistFlg.value === 1 ? '本登録' : '仮登録';
        const modeLabel = importMode === 'update' ? '更新' : '登録';
        successMessage.value = `IES4ファイルから測定報告書を${modeLabel}しました（${registLabel}／報告書ID: ${reportId}）`;
        store.commit('setFlashMessage', { message: `IES4取込が完了しました（${modeLabel}・${registLabel}）`, type: 'success' });

        router.push(`/building-measurement-reports/${customerId}/${buildingId}`);
      } catch (error) {
        errorMessages.value = extractErrorMessages(error);
        status.value = 'saveError';
        closeDuplicateModal();
        closePreview();
      }
    };

    const handleCommit = async () => {
      if (isSaveDisabled.value) return;

      // 同一ファイルの取込履歴がある場合は、更新か新規登録かを選んでもらう
      if (duplicateImport.value) {
        closePreview();
        getDuplicateModal()?.show();
        return;
      }

      await commitAs('create');
    };

    const openDeviceModal = (template) => {
      deviceFormTemplateKey.value = template.measure_type_key;
      deviceFormError.value = '';

      const saved = newDeviceInputs[template.measure_type_key];
      Object.assign(deviceForm, saved || {
        device_nm: template.instrument_name || '',
        manufacuturer: '',
        model_nm: template.instrument_name || '',
        serial_no: template.instrument_id || '',
        specification: ''
      });

      const element = deviceModalRef.value;
      if (!element) return;
      (Modal.getInstance(element) || new Modal(element)).show();
    };

    const saveDeviceForm = () => {
      if (!deviceForm.device_nm || !deviceForm.manufacuturer) {
        deviceFormError.value = '測定機器名と製造者は必須です';
        return;
      }

      const entered = { ...deviceForm };
      const source = usedDeviceTemplates.value.find(
        (template) => template.measure_type_key === deviceFormTemplateKey.value
      );

      // 同じ機器を使う測定項目にはまとめて反映する（IES4は1台で複数項目を測定する）
      usedDeviceTemplates.value.forEach((template) => {
        if (!template.will_create_device) return;
        if (source && template.raw_label !== source.raw_label) return;
        newDeviceInputs[template.measure_type_key] = entered;
      });

      deviceFormError.value = '';
      const element = deviceModalRef.value;
      if (element) (Modal.getInstance(element) || new Modal(element)).hide();
    };

    const formatDateTime = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString('ja-JP');
    };

    const goBack = () => {
      router.push({ name: 'CustomerBuildingList' });
    };

    // グラフはモーダル表示後でないと描画できないため、表示状態を子へ渡す
    const handleModalShown = () => { isPreviewVisible.value = true; };
    const handleModalHidden = () => { isPreviewVisible.value = false; };

    onMounted(async () => {
      previewModalRef.value?.addEventListener('shown.bs.modal', handleModalShown);
      previewModalRef.value?.addEventListener('hidden.bs.modal', handleModalHidden);

      try {
        const response = await getBuilding(buildingId);
        building.value = response.data;
      } catch (error) {
        errorMessages.value = ['対象建物の情報を取得できませんでした'];
      }

      // 建物一覧のモーダルで選択済みのファイルがあれば、そのまま自動解析する
      const pendingFile = takePendingIes4File(buildingId);
      if (pendingFile) await acceptFile(pendingFile);
    });

    onBeforeUnmount(() => {
      previewModalRef.value?.removeEventListener('shown.bs.modal', handleModalShown);
      previewModalRef.value?.removeEventListener('hidden.bs.modal', handleModalHidden);
    });

    return {
      manualUrl: IES4_IMPORT_MANUAL_URL,
      fileInputRef,
      previewModalRef,
      status,
      isBusy,
      isDragOver,
      hasResult,
      showFileSelector,
      isPreviewVisible,
      selectedFileName,
      errorMessages,
      successMessage,
      warnings,
      metadata,
      reportParams,
      measureResult,
      usedDeviceTemplates,
      previewUsedDevices,
      deviceOptions,
      measureTypeOptions,
      duplicateImport,
      duplicateReports,
      bookRegistFlg,
      selectedDeviceIds,
      selectedMeasureTypeIds,
      newDeviceInputs,
      deviceModalRef,
      duplicateModalRef,
      deviceForm,
      deviceFormError,
      canUpdateExisting,
      openDeviceModal,
      saveDeviceForm,
      commitAs,
      closeDuplicateModal,
      buildingName,
      customerName,
      buildingAddress,
      isPreviewDisabled,
      isSaveDisabled,
      handleFileChange,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      openPreview,
      handleCommit,
      deviceLabel,
      formatDateTime,
      goBack
    };
  }
};
</script>

<style scoped>
/* 操作説明PDFへのリンク。画面の主導線ではないため控えめに見せる */
.manual-link {
  font-size: 0.9rem;
  color: #2f4e6d;
  text-decoration: underline;
  white-space: nowrap;
}

.manual-link:hover {
  color: #1b2f42;
}

.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.target-building-box {
  border: 1px solid #d7dee7;
  background: #f8f9fa;
  padding: 12px 14px;
}

.target-building-box .label {
  color: #6c757d;
  font-size: 13px;
  margin-bottom: 4px;
}

.drop-area {
  border: 2px dashed #adb5bd;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  background: #fdfdfd;
}

.drop-area-active {
  border-color: #198754;
  background: #f1f9f4;
}

.drop-area-disabled {
  opacity: 0.6;
}

.selected-file {
  color: #198754;
}

.item-label {
  display: block;
  color: #6c757d;
  font-size: 12px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
</style>
